// src/components/security/RecoveryCodesPanel.tsx
/**
 * The one moment the ten recovery codes exist in plaintext outside a password manager.
 *
 * The server stores nothing but BCrypt hashes and has no endpoint that re-issues a set — the only
 * way to see codes again is to regenerate, which invalidates the ones the admin may have written
 * down. So this panel is built around a single assumption: the reader will close it too early. Hence
 * two ways to take the codes away rather than one (some browsers deny the clipboard, and a file
 * survives a closed tab), an explicit acknowledgement rather than a plain "Hotovo", and a copy
 * button that reports failure instead of claiming success it cannot verify.
 *
 * The panel deliberately does not own what happens next. `onAcknowledge` hands that back to the card
 * that issued the codes, which is where the store update and the status refresh belong.
 */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { RootState } from '../../store/store';
import { formatRecoveryCode } from '../../security/mfaContract';
import { copyText, saveTextFile } from './browser';
import { Banner, CheckRow, Codes, Hint, Mono, Primary, Row, Secondary, WarnBox } from './ui';

const FILENAME = 'goly-esa-zalozne-kody.txt';

const COPY_FAILED =
    'Kopírovanie sa nepodarilo. Označte kódy myšou a skopírujte ich ručne (Ctrl/Cmd + C).';

interface RecoveryCodesPanelProps {
    codes: string[];
    title: string;
    onAcknowledge: () => void;
}

type Notice = { text: string; tone: 'ok' | 'warn' };

/** "10. 9. 2026" — the date the file was written, so a set found in a password manager years later
 *  can be told apart from a newer one. */
function today(): string {
    const now = new Date();
    return now.getDate() + '. ' + (now.getMonth() + 1) + '. ' + now.getFullYear();
}

/**
 * What lands in the .txt. The header exists because a bare list of ten eight-character strings in a
 * downloads folder is unidentifiable — and because a set belonging to the wrong account, or
 * superseded by a later regeneration, is worse than no set at all.
 *
 * CRLF on purpose: this file is opened by whatever the admin's machine associates with .txt, and
 * older Windows viewers render a lone LF as one long line.
 */
function fileBody(codes: string[], email: string): string {
    const lines = [
        'Góly a Esá — záložné kódy pre dvojstupňové overenie',
        'Účet: ' + (email || 'neznámy'),
        'Vytvorené: ' + today(),
        '',
        'Každý kód platí len raz. Uložte tento súbor na bezpečné miesto a nikomu ho neposielajte.',
        '',
    ];
    for (let i = 0; i < codes.length; i++) {
        lines.push(formatRecoveryCode(codes[i]));
    }
    return lines.join('\r\n') + '\r\n';
}

const RecoveryCodesPanel: React.FC<RecoveryCodesPanelProps> = ({ codes, title, onAcknowledge }) => {
    const user = useSelector((state: RootState) => state.auth.user);
    const [saved, setSaved] = useState(false);
    const [notice, setNotice] = useState<Notice | null>(null);

    // A second set replaces the first: the acknowledgement has to be earned again, and a stale
    // "Skopírované." must not sit above codes that are no longer the ones on the clipboard.
    useEffect(() => {
        setSaved(false);
        setNotice(null);
    }, [codes]);

    const formatted: string[] = [];
    for (let i = 0; i < codes.length; i++) {
        formatted.push(formatRecoveryCode(codes[i]));
    }

    const handleCopy = async () => {
        const ok = await copyText(formatted.join('\n'));
        setNotice(ok ? { text: 'Skopírované.', tone: 'ok' } : { text: COPY_FAILED, tone: 'warn' });
    };

    const handleDownload = () => {
        saveTextFile(FILENAME, fileBody(codes, user ? user.email : ''));
        setNotice({ text: 'Súbor ' + FILENAME + ' sa stiahol.', tone: 'ok' });
    };

    return (
        <div>
            <PanelTitle>{title}</PanelTitle>

            <WarnBox>
                Uložte si ich na bezpečné miesto — do správcu hesiel alebo vytlačené mimo počítača.
                Každý kód platí len raz a už sa znova nezobrazia.
            </WarnBox>

            <Codes>
                {/* Keyed by position, not by value: the list is static and never reordered, and two
                    codes out of a 31-symbol alphabet can in principle repeat — a duplicate React key
                    would drop one of them from the grid, which on this panel means a lost code. */}
                {formatted.map((code, index) => (
                    <Mono key={index}>{code}</Mono>
                ))}
            </Codes>

            {notice && (
                <Banner $ok={notice.tone === 'ok'} $warn={notice.tone === 'warn'}>
                    {notice.text}
                </Banner>
            )}

            <Row>
                <Secondary type="button" onClick={handleCopy}>
                    Skopírovať
                </Secondary>
                <Secondary type="button" onClick={handleDownload}>
                    Stiahnuť .txt
                </Secondary>
            </Row>

            <Hint style={{ marginBottom: 16 }}>
                Kód použijete namiesto kódu z aplikácie, keď k nej stratíte prístup.
            </Hint>

            <CheckRow>
                <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
                Uložil som si záložné kódy.
            </CheckRow>

            <Primary type="button" disabled={!saved} onClick={onAcknowledge}>
                Hotovo
            </Primary>
        </div>
    );
};

const PanelTitle = styled.h3`
    font-size: 15px;
    font-weight: 700;
    color: #141210;
    margin: 0 0 10px;
`;

export default RecoveryCodesPanel;
